import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Canonical checksheet page URLs:
 *   Scroll to table:       /facilities/{slug}/checksheets#{checksheetId}
 *   Scroll + date filter:  /facilities/{slug}/checksheets?{YYYY-MM-DD}#{checksheetId}
 *   Open record:           /facilities/{slug}/checksheets?checksheet={id}&record={id}
 *   Record id only:         /facilities/{slug}/checksheets?record={id}
 */

const parsePositiveInt = value => {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

export const buildChecksheetScrollUrl = (facilitySlug, checksheetId) => {
  if (!facilitySlug || checksheetId == null) return null;
  return `/facilities/${facilitySlug}/checksheets#${checksheetId}`;
};

/** Legacy scroll + table date search (notify emails, post-record URL cleanup). */
export const buildChecksheetDateScrollUrl = (facilitySlug, dateStr, checksheetId) => {
  if (!facilitySlug || checksheetId == null) return null;
  if (!dateStr) return buildChecksheetScrollUrl(facilitySlug, checksheetId);
  return `/facilities/${facilitySlug}/checksheets?${dateStr}#${checksheetId}`;
};

export const buildChecksheetRecordUrl = (facilitySlug, {checksheetId, recordId}) => {
  if (!facilitySlug || recordId == null) return null;

  const params = new URLSearchParams();
  // checksheetId is optional: when omitted the page resolves the owning checksheet
  // from the record itself (record.facilityChecksheetId). This matters for components
  // shared across checksheets, where the viewing checksheet may not own the record.
  if (checksheetId != null) params.set("checksheet", String(checksheetId));
  params.set("record", String(recordId));

  return `/facilities/${facilitySlug}/checksheets?${params.toString()}`;
};

/** Date string for table record search in facility settings timezone (YYYY-MM-DD). */
export const formatRecordDateSearchQuery = (record, tz = "US/Pacific") => {
  const raw = record?.dateDue ?? record?.completedAt ?? record?.createdAt;
  if (!raw) return undefined;
  const zone = tz || "US/Pacific";
  return dayjs.utc(raw).tz(zone).format("YYYY-MM-DD");
};

export const hasStructuredDeepLinkSearch = search => {
  const params = new URLSearchParams((search || "").replace(/^\?/, ""));
  return params.has("checksheet") || params.has("record");
};

/** Legacy notify URL: ?YYYY-MM-DD#{checksheetId} — hash is a table id, never a record id. */
export const isLegacyDateScrollDeepLink = parsed =>
  Boolean(
    parsed?.searchQuery && parsed.checksheetId != null && parsed.recordId == null
  );

/**
 * @returns {{ checksheetId?: number, recordId?: number, searchQuery?: string }}
 */
export const parseChecksheetsDeepLink = ({search = "", hash = ""} = {}) => {
  const hashValue = (hash || "").replace(/^#/, "");
  const searchRaw = (search || "").replace(/^\?/, "");
  const params = new URLSearchParams(searchRaw);

  const searchQuery = params.get("q") || undefined;

  if (params.has("record") || params.has("checksheet")) {
    return {
      checksheetId: parsePositiveInt(params.get("checksheet")),
      recordId: parsePositiveInt(params.get("record")),
      searchQuery
    };
  }

  // Legacy: ?{dateOrDue}#{checksheetId} (e.g. notify_completed emails)
  if (searchRaw && hashValue && !searchRaw.includes("/")) {
    const legacyChecksheetId = parsePositiveInt(hashValue);
    if (legacyChecksheetId != null) {
      return {
        checksheetId: legacyChecksheetId,
        recordId: undefined,
        searchQuery: searchRaw
      };
    }
  }

  // Scroll / legacy hash-only: #{id}
  if (hashValue && !searchRaw) {
    const hashNum = parsePositiveInt(hashValue);
    if (hashNum != null) {
      return {checksheetId: hashNum, recordId: undefined, searchQuery};
    }
  }

  // Optional search with hash scroll: ?q=... or ?date with #{checksheetId}
  if (hashValue) {
    const hashNum = parsePositiveInt(hashValue);
    if (hashNum != null) {
      return {
        checksheetId: hashNum,
        recordId: undefined,
        searchQuery: searchRaw || searchQuery
      };
    }
  }

  return {searchQuery};
};
